import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from 'react-apollo';
import { Formik } from 'formik';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';
import pick from 'lodash.pick';
import omit from 'lodash.omit';

import { MeWithRosterIdentity } from '../../../graphql/queries/account';
import Spinner from '../Spinner';
import FormGroup from '../FormGroup';
import { getFormikTouchedErrors } from '../../../util/formik';
import Alert from '../Alert';
import Button from '../Button';
import {
  rosterIdentityCreateFromIdentity as rosterIdentityCreateFromIdentityMutation,
  rosterIdentityUpdate as rosterIdentityUpdateMutation,
} from '../../../graphql/mutations/roster-identity';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import Label from '../Label';
import useHierarchyGrades from '../hooks/useHierarchyGrades';
import {
  HIERARCHY_GRADES_ENUM,
  HIERARCHY_GRADES_ENUM_LABELS,
  HIERARCHY_GRADES_REQUIRING_CLASS,
} from '../../../constants/hierarchy-grades';
import useGlobalToast from '../../../hooks/useGlobalToast';
import {
  IdentityHasRosterIdentityDetails,
  IdentityRosterIdentityDetails,
} from '../../../graphql/fragments/identity';
import {
  getRosterIdentityLink,
  getRosterIdentityClassName,
  getRosterIdentityDepartment,
  getRosterIdentityGrade,
  getRosterIdentityOrganizationRefId,
  getRosterIdentityTeacher,
  getRosterIdentityTitle,
  getRosterIdentityPeriod,
  getRosterIdentityTrack,
} from '../../../util/identity';

const propTypes = {
  albumId: PropTypes.string,
  albumFolderId: PropTypes.string,
  rosterIdentityId: PropTypes.string,
  identity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    last_name: PropTypes.string,
  }),
  onRequestClose: PropTypes.func,
};

const YearbookRosterIdentity = ({
  albumId,
  albumFolderId,
  rosterIdentityId,
  identity,
  onRequestClose,
}) => {
  const openAlert = useOpenAlert();
  const { setToast } = useGlobalToast();
  const { grades } = useHierarchyGrades({ albumFolderId });
  const [saving, setSaving] = useState(false);
  const { data: { me: account } = {} } = useQuery(MeWithRosterIdentity, {
    variables: {
      roster_identity_id: rosterIdentityId,
    },
    skip: !rosterIdentityId,
  });

  const [rosterIdentityUpdate] = useMutation(rosterIdentityUpdateMutation);
  const [rosterIdentityCreateFromIdentity] = useMutation(
    rosterIdentityCreateFromIdentityMutation,
  );

  const rosterIdentity = useMemo(() => {
    return account?.roster_identities?.records?.[0];
  }, [account]);

  const gradeOptions = useMemo(
    () =>
      Object.values(HIERARCHY_GRADES_ENUM).filter(gradeEnum =>
        grades.find(hierarchyGrade => hierarchyGrade.grade === gradeEnum),
      ),
    [grades],
  );

  if (!rosterIdentity && rosterIdentityId) {
    return (
      <div className="text-center m-6 p-6">
        <Spinner />
      </div>
    );
  }

  if (!rosterIdentity && !identity) {
    return (
      <div className="text-center m-6 p-6">
        <Spinner />
      </div>
    );
  }

  const baseForm = {
    first_name: rosterIdentity?.first_name ?? identity?.name ?? '',
    last_name: rosterIdentity?.last_name ?? identity?.last_name ?? '',
    grade: getRosterIdentityGrade({
      rosterIdentity,
      albumId,
      albumFolderId,
      rawValue: true,
    }),
    class_name:
      getRosterIdentityClassName({
        rosterIdentity,
        albumId,
        albumFolderId,
      }) ?? '',
    teacher_name:
      getRosterIdentityTeacher({ rosterIdentity, albumId, albumFolderId }) ??
      '',
    department:
      getRosterIdentityDepartment({ rosterIdentity, albumId, albumFolderId }) ??
      '',
    period:
      getRosterIdentityPeriod({ rosterIdentity, albumId, albumFolderId }) ?? '',
    track:
      getRosterIdentityTrack({ rosterIdentity, albumId, albumFolderId }) ?? '',
    title:
      getRosterIdentityTitle({ rosterIdentity, albumId, albumFolderId }) ?? '',
    organization_ref_id:
      getRosterIdentityOrganizationRefId({
        rosterIdentity,
        albumId,
        albumFolderId,
      }) ?? '',
  };

  const onSubmit = async values => {
    if (saving) {
      return;
    }

    setSaving(true);

    try {
      let rosterIdentityIdToUse = rosterIdentityId;

      if (identity && !rosterIdentityId) {
        const {
          data: {
            rosterIdentityCreateFromIdentity: {
              roster_identity: newRosterIdentity,
            },
          },
        } = await rosterIdentityCreateFromIdentity({
          variables: {
            input: {
              identity_id: identity.id,
              linked_album_ids: albumId ? [albumId] : [],
              linked_album_folder_ids: albumId ? [] : [albumFolderId],
            },
          },
          update: (
            store,
            {
              data: {
                rosterIdentityCreateFromIdentity: {
                  roster_identity: createdRosterIdentity,
                },
              },
            },
          ) => {
            try {
              const data = store.readFragment({
                id: defaultDataIdFromObject({
                  id: identity.id,
                  __typename: 'Identity',
                }),
                fragment: IdentityHasRosterIdentityDetails,
                fragmentName: 'IdentityHasRosterIdentityDetails',
              });

              data.roster_identity = {
                ...pick(createdRosterIdentity, [
                  'id',
                  'organization_ref_id',
                  '__typename',
                ]),
              };

              store.writeFragment({
                id: defaultDataIdFromObject({
                  id: identity.id,
                  __typename: 'Identity',
                }),
                fragment: IdentityHasRosterIdentityDetails,
                fragmentName: 'IdentityHasRosterIdentityDetails',
                data,
              });
            } catch {
              //   Do nothing, query might not be loaded.
            }
          },
        });
        rosterIdentityIdToUse = newRosterIdentity.id;
      }
      await rosterIdentityUpdate({
        variables: {
          input: {
            roster_identity_id: rosterIdentityIdToUse,
            ...omit(values, [
              'grade',
              'class_name',
              'teacher_name',
              'department',
              'period',
              'track',
              'title',
              'organization_ref_id',
            ]),
            update_links: [albumId, albumFolderId]
              .filter(Boolean)
              .map(hierarchyItemId => {
                const existingLink = getRosterIdentityLink({
                  rosterIdentity,
                  albumId: hierarchyItemId,
                  albumFolderId: hierarchyItemId,
                });

                if (!existingLink) {
                  return undefined;
                }

                return {
                  roster_identity_link_id: existingLink.id,
                  grade: values.grade,
                  teacher_name: values.teacher_name,
                  class_name: values.class_name,
                  department: values.department,
                  period: values.period,
                  track: values.track,
                  title: values.title,
                  ref_id: values.organization_ref_id,
                };
              })
              .filter(Boolean),
          },
        },
        update: (
          store,
          {
            data: {
              rosterIdentityUpdate: { roster_identity: updatedRosterIdentity },
            },
          },
        ) => {
          try {
            const data = store.readFragment({
              id: defaultDataIdFromObject({
                id: identity.id,
                __typename: 'Identity',
              }),
              fragment: IdentityRosterIdentityDetails,
              fragmentName: 'IdentityRosterIdentityDetails',
            });

            data.roster_identity = updatedRosterIdentity;

            store.writeFragment({
              id: defaultDataIdFromObject({
                id: identity.id,
                __typename: 'Identity',
              }),
              fragment: IdentityRosterIdentityDetails,
              fragmentName: 'IdentityRosterIdentityDetails',
              data,
            });
          } catch {
            //   Do nothing, query might not be loaded.
          }
        },
      });
      setToast('Student information updated successfully!');
      onRequestClose?.();
    } catch (e) {
      openAlert({
        message: 'Error updating student data.',
        info: getErrorString(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <h4 className="text-center mb-3">Student data</h4>
      <Formik
        initialValues={baseForm}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validateOnMount={false}
        render={({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => {
          const errorList = Object.values(
            getFormikTouchedErrors(errors, touched),
          );
          const gradeRequiresTeacher = HIERARCHY_GRADES_REQUIRING_CLASS.includes(
            values.grade,
          );

          return (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-12 col-md-6">
                  <FormGroup
                    id="first_name"
                    name="first_name"
                    label="First Name"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FormGroup
                    id="last_name"
                    name="last_name"
                    label="Last Name"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Label id="grade">Grade</Label>
                  <select
                    id="grade"
                    name="grade"
                    value={values.grade}
                    className="form-control"
                    onChange={handleChange}
                    placeholder="Select a grade..."
                  >
                    <option value="">Select a grade...</option>
                    {gradeOptions.map(gradeEnum => (
                      <option key={gradeEnum} value={gradeEnum}>
                        {HIERARCHY_GRADES_ENUM_LABELS[gradeEnum]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <FormGroup
                    id="teacher_name"
                    name="teacher_name"
                    label="Teacher Name"
                    disabled={!gradeRequiresTeacher}
                    required={gradeRequiresTeacher}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.teacher_name}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FormGroup
                    id="department"
                    name="department"
                    label="Department"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.department}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FormGroup
                    id="period"
                    name="period"
                    label="Period"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.period}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FormGroup
                    id="track"
                    name="track"
                    label="Track"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.track}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FormGroup
                    id="title"
                    name="title"
                    label="Title"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.title}
                  />
                </div>
                <div className="col-12">
                  <FormGroup
                    id="organization_ref_id"
                    name="organization_ref_id"
                    label="Student ID"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.organization_ref_id}
                  />
                </div>
              </div>
              {errorList.length > 0 && (
                <Alert type="danger" className="mt-2">
                  <ul>
                    {errorList.map(error => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              <div className="text-center mt-2">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Update'}
                </Button>
              </div>
            </form>
          );
        }}
      />
    </Fragment>
  );
};

YearbookRosterIdentity.propTypes = propTypes;

export default YearbookRosterIdentity;
